// src/components/Attachments/Attachments.js

import React, { useEffect, useRef, useState } from 'react';
import './scss/Attachments.scss';
import { useDispatch, useSelector } from 'react-redux';
import { timeAgo, getFileExtension, isImage } from '../../utils/helpers';
import {
    fetchAttachments,
    uploadAttachment,
    editAttachment,
    deleteAttachment,
    resetStatus,
} from '../../redux/Slices/attachmentSlice';

import { togglePopover } from '../../redux/Slices/popoverSlice';

import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import { hideSnackBar, showSnackBar } from '../../redux/Slices/snackbarSlice';
import { showUploading, updateUploadProgress, completeUploadProgress } from '../../utils/snackbarHelpers';

import { fetchAttachmentUrl } from '../../utils/attachmentUtils';

import '../Global/scss/ImageSquareLoader.scss';


const Attachments = ({ cardId }) => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const { attachments, status } = useSelector((state) => state.attachments);

    const error = useSelector((state) => state.attachments.error);

    const { handlePopoverClick } = useHandlePopoverClick();

    // Per-attachment runtime state for broken/unavailable attachments. Tracks
    // whether we've already attempted a refresh (to stop the infinite onError
    // → refresh → onError loop) and whether the file is gone from storage.
    const [attachmentState, setAttachmentState] = useState({});

    const markUnavailable = (id) =>
        setAttachmentState((s) => ({ ...s, [id]: { ...(s[id] || {}), unavailable: true, attempted: true } }));
    const markAttempted = (id) =>
        setAttachmentState((s) => ({ ...s, [id]: { ...(s[id] || {}), attempted: true } }));

    useEffect(() => {

        if (status === 'uploadSucceeded') {

            dispatch(fetchAttachments(cardId)).finally(() => {
                dispatch(resetStatus());
                dispatch(hideSnackBar());

            });
        }
    }, [status, dispatch, cardId]);


    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            // Start upload progress tracking
            const uploadId = showUploading(dispatch, 'Uploading files...');

            // Set global upload ID for WebSocket progress tracking
            window.currentUploadId = uploadId;

            try {
                const response = await dispatch(uploadAttachment({
                    file,
                    cardId,
                    uploadId
                }));

                // Clear global upload ID
                window.currentUploadId = null;

                if (response.meta.requestStatus !== "fulfilled") {
                    throw new Error(response.payload?.message || "File upload failed.");
                }

                // Complete upload with proper progress display
                completeUploadProgress(dispatch, uploadId);
            } catch (error) {
                // Clear global upload ID on error
                window.currentUploadId = null;

                dispatch(hideSnackBar({ id: uploadId }));
                dispatch(showSnackBar({ message: error.message || 'Upload failed', type: 'error' }));
            }
        }
    };

    const handleAttachmentEdit = (attachmentId, newName) => {
        dispatch(editAttachment({ cardId, attachmentId, newName }));
    };

    const handleAttachmentDelete = (e, attachment) => {
        handlePopoverClick(e, 'deleteAction', { 
            type: 'ATTACHMENT', 
            id: attachment._id, 
            cardId,
            attachmentName: attachment.name
        });
    };

    const handleAttachmentImageError = async (e, attachment) => {
        const img = e?.target;
        if (!img) return;

        // Only attempt one refresh per attachment per mount. If the freshly
        // signed URL also fails to load, treat the attachment as unavailable
        // rather than looping forever.
        if (attachmentState[attachment._id]?.attempted) {
            markUnavailable(attachment._id);
            return;
        }
        markAttempted(attachment._id);

        const rect = img.getBoundingClientRect();
        const minW = Math.max(120, Math.floor(rect.width || 0));
        const minH = Math.max(90, Math.floor(rect.height || 0));

        const loader = document.createElement('div');
        loader.className = 'image-loading-placeholder image-refetch-loader';
        loader.style.minWidth = `${minW}px`;
        loader.style.minHeight = `${minH}px`;
        loader.style.width = rect.width ? `${Math.floor(rect.width)}px` : '';
        loader.style.height = rect.height ? `${Math.floor(rect.height)}px` : '';
        loader.innerHTML = '<div class="image-loading-square"></div>';

        img.style.visibility = 'hidden';
        img.after(loader);

        try {
            const result = await fetchAttachmentUrl(cardId, attachment._id);
            if (result?.deleted || !result?.url) {
                markUnavailable(attachment._id);
                return;
            }
            img.src = result.url;
            const item = img.closest('.attachment-item');
            const link = item && item.querySelector('a.attachment-download');
            if (link) link.href = result.url;
        } catch (err) {
            markUnavailable(attachment._id);
        } finally {
            loader.remove();
            img.style.visibility = '';
        }
    };

    return (
        <div className="attachments">

            <div className="attachments-header">
                <h3 className="attachments-title">
                    <span className="material-symbols-outlined">
                        attach_file
                    </span>
                    Attachments
                </h3>

                <button className="upload-button" onClick={() => fileInputRef.current.click()}>
                    <span className="material-symbols-outlined">
                        attach_file_add
                    </span>
                    Attach File</button>
            </div>

            <ul className="attachments-list">
                {attachments?.map((attachment) => {
                    const unavailable = attachment.deleted || attachmentState[attachment._id]?.unavailable;

                    return (
                        <li key={attachment._id} className={`attachment-item${unavailable ? ' attachment-unavailable' : ''}`}>
                            <div className="attachment-icon">
                                {unavailable ? (
                                    <span className="icon" title="Attachment unavailable">
                                        <span className="material-symbols-outlined">broken_image</span>
                                    </span>
                                ) : isImage(attachment.url) ? (
                                    <img src={attachment.url} alt={attachment.name} onError={(e) => handleAttachmentImageError(e, attachment)} loading="lazy" />
                                ) : (
                                    <span className="icon">
                                        {getFileExtension(attachment.url)}
                                    </span>

                                )}
                            </div>

                            <div className="attachment-info">
                                <div className='attachment-wrapper'>
                                    <div className="attachment-name">
                                        {attachment.name}
                                        {unavailable && (
                                            <span className="attachment-status-tag" aria-label="Attachment unavailable"> · Unavailable</span>
                                        )}
                                    </div>
                                    <div className="attachment-actiion-buttons">
                                        {!unavailable && (
                                            <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="attachment-download action-icon">
                                                <span className="material-symbols-outlined">
                                                    download
                                                </span>
                                            </a>
                                        )}
                                        <div className="attachment-comment action-icon">
                                            <span className="material-symbols-outlined">
                                                comment
                                            </span>
                                        </div>
                                        <div className="attachment-delete action-icon" id={`popover-attachment-delete-${attachment._id}`} onClick={(e) => handleAttachmentDelete(e, attachment)} data-popover-trigger>
                                            <span className="material-symbols-outlined">
                                                delete
                                            </span>
                                        </div>
                                        <div className="attachment-edit action-icon" onClick={() => {
                                            const newName = prompt("Enter new name:", attachment.name);
                                            if (newName) handleAttachmentEdit(attachment._id, newName);
                                        }}>
                                            <span className="material-symbols-outlined">
                                                edit_square
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="attachment-details">
                                    <span>Added {timeAgo(attachment.uploadedAt)}</span>
                                </div>
                            </div>
                        </li>
                    )
                })}
            </ul>
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileUpload}
            />
        </div>
    );
};

export default Attachments;
